import {
  TABLE_KEYS,
  TABLES,
  FOREIGN_KEYS,
  emptyCollections,
  createAuditLog,
} from "./schema";
import { emitJourneyChange } from "./live";

let cache = null;

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function pushIndex(map, key, id) {
  if (key == null || key === "") return;
  const list = map.get(key) || [];
  list.push(id);
  map.set(key, list);
}

export function buildIndexes(collections) {
  const indexes = {
    usersById: new Map(),
    usersByEmail: new Map(),
    usersByRole: new Map(),
    transactionsById: new Map(),
    transactionsByBrokerId: new Map(),
    transactionsByAgentId: new Map(),
    transactionsByClientId: new Map(),
    threadsById: new Map(),
    threadsByTransactionId: new Map(),
    messagesById: new Map(),
    messagesByThreadId: new Map(),
    attachmentsByMessageId: new Map(),
    exportsByTransactionId: new Map(),
    archivesByTransactionId: new Map(),
    notificationsByUserId: new Map(),
    auditByTransactionId: new Map(),
    auditByUserId: new Map(),
    betaFeedbackByUserId: new Map(),
  };

  collections.users.forEach((row) => {
    indexes.usersById.set(row.id, row);
    if (row.email) indexes.usersByEmail.set(row.email, row);
    pushIndex(indexes.usersByRole, row.role_type, row.id);
  });

  collections.transactions.forEach((row) => {
    indexes.transactionsById.set(row.id, row);
    pushIndex(indexes.transactionsByBrokerId, row.broker_id, row.id);
    pushIndex(indexes.transactionsByAgentId, row.agent_id, row.id);
    pushIndex(indexes.transactionsByClientId, row.client_id, row.id);
  });

  collections.message_threads.forEach((row) => {
    indexes.threadsById.set(row.id, row);
    pushIndex(indexes.threadsByTransactionId, row.transaction_id, row.id);
  });

  const messages = [...collections.messages].sort((a, b) =>
    String(a.timestamp).localeCompare(String(b.timestamp))
  );
  messages.forEach((row) => {
    indexes.messagesById.set(row.id, row);
    pushIndex(indexes.messagesByThreadId, row.thread_id, row.id);
  });

  collections.attachments.forEach((row) => {
    pushIndex(indexes.attachmentsByMessageId, row.message_id, row.id);
  });
  collections.exports.forEach((row) => {
    pushIndex(indexes.exportsByTransactionId, row.transaction_id, row.id);
  });
  collections.archive_records.forEach((row) => {
    pushIndex(indexes.archivesByTransactionId, row.transaction_id, row.id);
  });
  collections.notifications.forEach((row) => {
    pushIndex(indexes.notificationsByUserId, row.user_id, row.id);
  });
  collections.audit_logs.forEach((row) => {
    pushIndex(indexes.auditByTransactionId, row.transaction_id, row.id);
    pushIndex(indexes.auditByUserId, row.user_id, row.id);
  });
  (collections.beta_feedback || []).forEach((row) => {
    pushIndex(indexes.betaFeedbackByUserId, row.user_id, row.id);
  });

  return indexes;
}

export function readCollections() {
  const collections = emptyCollections();
  Object.keys(TABLES).forEach((table) => {
    collections[table] = readJson(TABLE_KEYS[table], []);
  });
  return collections;
}

function persistCollections(collections) {
  Object.keys(TABLES).forEach((table) => {
    writeJson(TABLE_KEYS[table], collections[table] || []);
  });
  emitJourneyChange();
}

const BLOBS_KEY = "dh_j_blobs";

export function putBlob(path, data) {
  const blobs = readJson(BLOBS_KEY, {});
  blobs[path] = data;
  writeJson(BLOBS_KEY, blobs);
  return path;
}

export function getBlob(path) {
  const blobs = readJson(BLOBS_KEY, {});
  return blobs[path] ?? null;
}

export function getSnapshot() {
  if (!cache) {
    const collections = readCollections();
    cache = { collections, indexes: buildIndexes(collections) };
  }
  return cache;
}

export function invalidateDbCache() {
  cache = null;
}

function assertForeignKeys(table, record, collections) {
  const rules = FOREIGN_KEYS[table] || [];
  rules.forEach((rule) => {
    const value = record[rule.field];
    if (!value) {
      throw new Error(`${table}.${rule.field} is required`);
    }
    const found = collections[rule.table].some((row) => row.id === value);
    if (!found) {
      throw new Error(`${table}.${rule.field} references missing ${rule.table} id ${value}`);
    }
  });
}

export function insertRow(table, record) {
  const db = getSnapshot();
  assertForeignKeys(table, record, db.collections);
  db.collections[table].push(record);
  persistCollections(db.collections);
  cache = { collections: db.collections, indexes: buildIndexes(db.collections) };
  return record;
}

export function updateRow(table, id, patch) {
  const db = getSnapshot();
  const list = db.collections[table];
  const index = list.findIndex((row) => row.id === id);
  if (index === -1) throw new Error(`${table} ${id} not found`);
  const next = { ...list[index], ...patch, id };
  assertForeignKeys(table, next, db.collections);
  list[index] = next;
  persistCollections(db.collections);
  cache = { collections: db.collections, indexes: buildIndexes(db.collections) };
  return next;
}

export function upsertRow(table, record) {
  const db = getSnapshot();
  const list = db.collections[table];
  const index = list.findIndex((row) => row.id === record.id);
  if (index === -1) {
    return insertRow(table, record);
  }
  return updateRow(table, record.id, record);
}

export function getById(table, id) {
  const { collections } = getSnapshot();
  return collections[table].find((row) => row.id === id) || null;
}

export function listTable(table) {
  return getSnapshot().collections[table];
}

export function idsFromIndex(indexName, key) {
  if (key == null) return [];
  return getSnapshot().indexes[indexName].get(key) || [];
}

export function rowsFromIndex(table, indexName, key) {
  const { collections, indexes } = getSnapshot();
  const ids = indexes[indexName].get(key) || [];
  const byId = new Map(collections[table].map((row) => [row.id, row]));
  return ids.map((id) => byId.get(id)).filter(Boolean);
}

export function writeAudit(entry) {
  return insertRow("audit_logs", createAuditLog(entry));
}

export function replaceCollections(collections) {
  persistCollections(collections);
  cache = { collections, indexes: buildIndexes(collections) };
  return cache;
}
